/**
 * Browser Feature Detection
 * 
 * Detects browser capabilities for WebSocket client optimization.
 * Supports detection of:
 * - WebSocket support
 * - Page Visibility API
 * - Network Information API
 * - Browser type and version
 * - Mobile device detection
 * 
 * @module browserDetection
 */

/**
 * Browser capabilities interface
 */
export interface BrowserCapabilities {
  /** Whether the browser supports WebSocket */
  hasWebSocket: boolean;
  /** Whether the browser supports Page Visibility API */
  hasPageVisibility: boolean;
  /** Whether the browser supports Network Information API */
  hasNetworkInformation: boolean;
  /** Whether the device is a mobile device */
  isMobile: boolean;
  /** Whether the device is a tablet */
  isTablet: boolean;
  /** Whether running in iOS environment */
  isIOS: boolean;
  /** Whether running in Android environment */
  isAndroid: boolean;
  /** Whether running in a PWA context */
  isPWA: boolean;
  /** Whether running in a WebView context */
  isWebView: boolean;
  /** Browser name */
  browserName: BrowserName;
  /** Browser version string */
  browserVersion: string;
  /** Browser major version number */
  browserMajorVersion: number;
  /** Whether the browser is supported */
  isSupported: boolean;
  /** Reason if browser is not supported */
  unsupportedReason?: string;
}

/**
 * Supported browser names
 */
export type BrowserName =
  | 'chrome'
  | 'firefox'
  | 'safari'
  | 'edge'
  | 'opera'
  | 'samsung'
  | 'ie'
  | 'unknown';

/**
 * Minimum supported browser versions
 * Based on Requirement 16: 浏览器兼容性
 */
export const MIN_BROWSER_VERSIONS: Record<BrowserName, number> = {
  chrome: 90,
  firefox: 88,
  safari: 14,
  edge: 90,
  opera: 76, // Based on Chromium 90
  samsung: 14,
  ie: Infinity, // IE is not supported
  unknown: 0
};

/**
 * Network connection type
 */
export type NetworkConnectionType =
  | 'slow-2g'
  | '2g'
  | '3g'
  | '4g'
  | 'unknown';

/**
 * Network information interface
 */
export interface NetworkInfo {
  /** Whether online */
  isOnline: boolean;
  /** Connection type (if available) */
  connectionType: NetworkConnectionType;
  /** Effective connection type */
  effectiveType?: string;
  /** Downlink speed in Mbps */
  downlink?: number;
  /** Round-trip time in ms */
  rtt?: number;
  /** Whether data saver is enabled */
  saveData?: boolean;
}

/**
 * Extended Navigator interface with Network Information API
 */
interface NavigatorWithConnection extends Navigator {
  connection?: {
    type?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
    addEventListener?: (type: string, listener: EventListener) => void;
    removeEventListener?: (type: string, listener: EventListener) => void;
  };
  mozConnection?: NavigatorWithConnection['connection'];
  webkitConnection?: NavigatorWithConnection['connection'];
}

/**
 * Detect if WebSocket is supported
 */
export function detectWebSocketSupport(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  return 'WebSocket' in window && typeof WebSocket !== 'undefined';
}

/**
 * Detect if Page Visibility API is supported
 */
export function detectPageVisibilitySupport(): boolean {
  if (typeof document === 'undefined') {
    return false;
  }

  return 'hidden' in document ||
    'webkitHidden' in document ||
    'mozHidden' in document ||
    'msHidden' in document;
}

/**
 * Get the correct visibility property name for the current browser
 */
export function getVisibilityPropertyName(): string {
  if (typeof document === 'undefined') {
    return 'hidden';
  }

  if ('hidden' in document) {
    return 'hidden';
  } else if ('webkitHidden' in document) {
    return 'webkitHidden';
  } else if ('mozHidden' in document) {
    return 'mozHidden';
  } else if ('msHidden' in document) {
    return 'msHidden';
  }

  return 'hidden';
}

/**
 * Get the correct visibility change event name for the current browser
 */
export function getVisibilityChangeEventName(): string {
  if (typeof document === 'undefined') {
    return 'visibilitychange';
  }

  if ('hidden' in document) {
    return 'visibilitychange';
  } else if ('webkitHidden' in document) {
    return 'webkitvisibilitychange';
  } else if ('mozHidden' in document) {
    return 'mozvisibilitychange';
  } else if ('msHidden' in document) {
    return 'msvisibilitychange';
  }

  return 'visibilitychange';
}

/**
 * Check if the page is currently visible
 */
export function isPageVisible(): boolean {
  if (typeof document === 'undefined') {
    return true;
  }

  const prop = getVisibilityPropertyName();
  return !(document as any)[prop];
}

/**
 * Detect if Network Information API is supported
 */
export function detectNetworkInformationSupport(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const nav = navigator as NavigatorWithConnection;
  return !!(nav.connection || nav.mozConnection || nav.webkitConnection);
}

/**
 * Get the network connection object
 */
export function getNetworkConnection(): NavigatorWithConnection['connection'] | null {
  if (typeof navigator === 'undefined') {
    return null;
  }

  const nav = navigator as NavigatorWithConnection;
  return nav.connection || nav.mozConnection || nav.webkitConnection || null;
}

/**
 * Get current network information
 */
export function getNetworkInfo(): NetworkInfo {
  const isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
  const connection = getNetworkConnection();

  if (!connection) {
    return {
      isOnline,
      connectionType: 'unknown'
    };
  }

  return {
    isOnline,
    connectionType: (connection.effectiveType as NetworkConnectionType) || 'unknown',
    effectiveType: connection.effectiveType,
    downlink: connection.downlink,
    rtt: connection.rtt,
    saveData: connection.saveData
  };
}

/**
 * Detect browser name from user agent
 */
export function detectBrowserName(): BrowserName {
  if (typeof navigator === 'undefined') {
    return 'unknown';
  }

  const ua = navigator.userAgent.toLowerCase();

  // Order matters - check more specific browsers first

  // Samsung Internet
  if (ua.includes('samsungbrowser')) {
    return 'samsung';
  }

  // Edge (Chromium-based)
  if (ua.includes('edg/') || ua.includes('edge/')) {
    return 'edge';
  }

  // Opera
  if (ua.includes('opr/') || ua.includes('opera')) {
    return 'opera';
  }

  // Chrome (must check after Edge and Opera since they include 'chrome')
  if (ua.includes('chrome') && !ua.includes('chromium')) {
    return 'chrome';
  }

  // Firefox
  if (ua.includes('firefox') || ua.includes('fxios')) {
    return 'firefox';
  }

  // Safari (must check after Chrome since Chrome on iOS includes 'safari')
  if (ua.includes('safari') && !ua.includes('chrome')) {
    return 'safari';
  }

  // Internet Explorer
  if (ua.includes('msie') || ua.includes('trident')) {
    return 'ie';
  }

  return 'unknown';
}

/**
 * Detect browser version from user agent
 */
export function detectBrowserVersion(): string {
  if (typeof navigator === 'undefined') {
    return '0';
  }

  const ua = navigator.userAgent;
  const browserName = detectBrowserName();

  let versionMatch: RegExpMatchArray | null = null;

  switch (browserName) {
    case 'chrome':
      versionMatch = ua.match(/Chrome\/(\d+(?:\.\d+)*)/i);
      break;
    case 'firefox':
      versionMatch = ua.match(/Firefox\/(\d+(?:\.\d+)*)/i) ||
        ua.match(/FxiOS\/(\d+(?:\.\d+)*)/i);
      break;
    case 'safari':
      versionMatch = ua.match(/Version\/(\d+(?:\.\d+)*)/i);
      break;
    case 'edge':
      versionMatch = ua.match(/Edg\/(\d+(?:\.\d+)*)/i) ||
        ua.match(/Edge\/(\d+(?:\.\d+)*)/i);
      break;
    case 'opera':
      versionMatch = ua.match(/OPR\/(\d+(?:\.\d+)*)/i) ||
        ua.match(/Opera\/(\d+(?:\.\d+)*)/i);
      break;
    case 'samsung':
      versionMatch = ua.match(/SamsungBrowser\/(\d+(?:\.\d+)*)/i);
      break;
    case 'ie':
      versionMatch = ua.match(/MSIE (\d+(?:\.\d+)*)/i) ||
        ua.match(/rv:(\d+(?:\.\d+)*)/i);
      break;
    default:
      versionMatch = null;
  }

  return versionMatch ? versionMatch[1] : '0';
}

/**
 * Parse major version number from version string
 */
export function parseMajorVersion(version: string): number {
  const parts = version.split('.');
  const major = parseInt(parts[0], 10);
  return isNaN(major) ? 0 : major;
}

/**
 * Detect if device is mobile
 */
export function detectMobileDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();

  // Check for mobile keywords
  const mobileKeywords = [
    'mobile',
    'android',
    'iphone',
    'ipod',
    'blackberry',
    'windows phone',
    'opera mini',
    'opera mobi',
    'iemobile'
  ];

  return mobileKeywords.some(keyword => ua.includes(keyword));
}

/**
 * Detect if device is tablet
 */
export function detectTabletDevice(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();

  // iPad detection
  if (ua.includes('ipad')) {
    return true;
  }

  // iPad on iOS 13+ reports as Mac
  if (ua.includes('macintosh') && 'ontouchend' in document) {
    return true;
  }

  // Android tablet (has 'android' but not 'mobile')
  if (ua.includes('android') && !ua.includes('mobile')) {
    return true;
  }

  return false;
}

/**
 * Detect if running on iOS
 */
export function detectIOS(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();

  // Standard iOS detection
  if (/iphone|ipad|ipod/.test(ua)) {
    return true;
  }

  // iPad on iOS 13+ detection
  if (ua.includes('macintosh') && 'ontouchend' in document) {
    return true;
  }

  return false;
}

/**
 * Detect if running on Android
 */
export function detectAndroid(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return navigator.userAgent.toLowerCase().includes('android');
}

/**
 * Detect if running as PWA (Progressive Web App)
 */
export function detectPWA(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  // Check display-mode media query
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

  // Check iOS standalone mode
  const isIOSStandalone = (navigator as any).standalone === true;

  // Check if launched from home screen (Android)
  const isAndroidTWA = document.referrer.includes('android-app://');

  return isStandalone || isIOSStandalone || isAndroidTWA;
}

/**
 * Detect if running in WebView
 */
export function detectWebView(): boolean {
  if (typeof navigator === 'undefined') {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();

  // iOS WebView detection
  // WKWebView doesn't include 'Safari' in UA
  if (detectIOS() && !ua.includes('safari')) {
    return true;
  }

  // Android WebView detection
  if (ua.includes('wv') || ua.includes('; wv)')) {
    return true;
  }

  // Facebook/Instagram in-app browser
  if (ua.includes('fban') || ua.includes('fbav') || ua.includes('instagram')) {
    return true;
  }

  // WeChat browser
  if (ua.includes('micromessenger')) {
    return true;
  }

  // Line browser
  if (ua.includes('line/')) {
    return true;
  }

  return false;
}

/**
 * Check if browser version is supported
 */
export function isBrowserSupported(browserName: BrowserName, majorVersion: number): boolean {
  const minVersion = MIN_BROWSER_VERSIONS[browserName];

  // IE is never supported
  if (browserName === 'ie') {
    return false;
  }

  // Unknown browsers are allowed (will use feature detection)
  if (browserName === 'unknown') {
    return true;
  }

  return majorVersion >= minVersion;
}

/**
 * Get unsupported reason message
 */
export function getUnsupportedReason(
  browserName: BrowserName,
  majorVersion: number,
  hasWebSocket: boolean
): string | undefined {
  if (!hasWebSocket) {
    return 'WebSocket is not supported in this browser';
  }

  if (browserName === 'ie') {
    return 'Internet Explorer is not supported. Please use a modern browser.';
  }

  const minVersion = MIN_BROWSER_VERSIONS[browserName];
  if (majorVersion < minVersion && browserName !== 'unknown') {
    return `${browserName} version ${majorVersion} is not supported. Please upgrade to version ${minVersion} or higher.`;
  }

  return undefined;
}

/**
 * Detect all browser capabilities
 * Main entry point for browser detection
 */
export function detectBrowserCapabilities(): BrowserCapabilities {
  const hasWebSocket = detectWebSocketSupport();
  const hasPageVisibility = detectPageVisibilitySupport();
  const hasNetworkInformation = detectNetworkInformationSupport();
  const isMobile = detectMobileDevice();
  const isTablet = detectTabletDevice();
  const isIOS = detectIOS();
  const isAndroid = detectAndroid();
  const isPWA = detectPWA();
  const isWebView = detectWebView();
  const browserName = detectBrowserName();
  const browserVersion = detectBrowserVersion();
  const browserMajorVersion = parseMajorVersion(browserVersion);
  const isSupported = hasWebSocket && isBrowserSupported(browserName, browserMajorVersion);
  const unsupportedReason = getUnsupportedReason(browserName, browserMajorVersion, hasWebSocket);

  return {
    hasWebSocket,
    hasPageVisibility,
    hasNetworkInformation,
    isMobile,
    isTablet,
    isIOS,
    isAndroid,
    isPWA,
    isWebView,
    browserName,
    browserVersion,
    browserMajorVersion,
    isSupported,
    unsupportedReason
  };
}

/**
 * Cached browser capabilities (singleton)
 */
let cachedCapabilities: BrowserCapabilities | null = null;

/**
 * Get browser capabilities (cached)
 * Use this for performance when capabilities are needed multiple times
 */
export function getBrowserCapabilities(): BrowserCapabilities {
  if (!cachedCapabilities) {
    cachedCapabilities = detectBrowserCapabilities();
  }
  return cachedCapabilities;
}

/**
 * Clear cached capabilities
 * Useful for testing or when browser environment changes
 */
export function clearCapabilitiesCache(): void {
  cachedCapabilities = null;
}

/**
 * Get recommended Socket.IO transport based on browser capabilities
 */
export function getRecommendedTransports(capabilities: BrowserCapabilities): ('websocket' | 'polling')[] {
  // If WebSocket is not supported, use polling only
  if (!capabilities.hasWebSocket) {
    return ['polling'];
  }

  // For WebView environments, prefer polling as WebSocket may be restricted
  if (capabilities.isWebView) {
    return ['polling', 'websocket'];
  }

  // Default: prefer WebSocket, fallback to polling
  return ['websocket', 'polling'];
}

/**
 * Get recommended reconnection delay based on network conditions
 */
export function getRecommendedReconnectDelay(networkInfo: NetworkInfo): number {
  // If offline, use longer delay
  if (!networkInfo.isOnline) {
    return 10000; // 10 seconds
  }

  // Adjust based on connection type
  switch (networkInfo.connectionType) {
    case 'slow-2g':
      return 5000; // 5 seconds
    case '2g':
      return 3000; // 3 seconds
    case '3g':
      return 2000; // 2 seconds
    case '4g':
    default:
      return 1000; // 1 second
  }
}

/**
 * Check if should use aggressive reconnection
 * (e.g., on mobile with good connection)
 */
export function shouldUseAggressiveReconnect(
  capabilities: BrowserCapabilities,
  networkInfo: NetworkInfo
): boolean {
  // Don't be aggressive on slow connections
  if (networkInfo.connectionType === 'slow-2g' || networkInfo.connectionType === '2g') {
    return false;
  }

  // Don't be aggressive if data saver is enabled
  if (networkInfo.saveData) {
    return false;
  }

  // Be aggressive on desktop or good mobile connections
  return !capabilities.isMobile || networkInfo.connectionType === '4g';
}
